// src/oura/components/PageWorkshopView.tsx
//
// One page, extracted in FULL, with every improvement Oura proposes for it
// highlighted in place — in the flow of the copy it changes.
//
// THE IDEA THIS SURFACE IS BUILT ON: a change can only be judged in the flow
// of the whole page. So the left pane is the entire document, block by block,
// with a gutter naming what each block is (h1, p, img — the Surfer-style
// treatment), images shown as labelled placeholders, and the exact text spans
// Oura wants to touch highlighted inline. Clicking a highlight opens the
// proposal for it on the right. Blocks with nothing proposed still render —
// the reviewer reads everything, which is the point.
//
// ONE ACCENT, ONE CARD SHAPE. Every proposal — copy or link — reads the same
// way: the passage as it is, the passage as it would be, one plain paragraph
// of why. A link proposal is shown as the sentence GAINING a link (anchor
// underlined, destination named beneath it), not as a stack of labelled
// boxes. Blue is the only accent; state and diagnosis are words, not colors.
//
// INCLUDED BY DEFAULT, DISMISS TO OPT OUT. Every proposal counts as accepted
// the moment Oura writes it; the reviewer's job is to take things OUT, not to
// tick each one in. An "Accept" button implied nothing happened without a
// click — the opposite of a platform that does the work and asks to be
// corrected. Dismissed proposals leave the draft and are not raised again;
// a note asks for a different take on the next redraft. (On the wire the
// notes ride the Redraft click as `notes[]` on POST /pages/draft — resolved
// against the proposals they name and injected into the prompt, outranking
// the model's earlier choices. Until that click they live in the parent
// screen, mirrored to localStorage so a reload does not eat them.)
//
// A FULL SURFACE, NOT AN OVERLAY. This started as a drawer, but at the width
// the two panes need it covered most of the screen — an overlay pretending to
// be a page. The work step is a step, so it REPLACES the crawled-pages list
// inside the Pages tab and offers a way back, instead of floating over it.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    XIcon,
    SparklesIcon,
    LinkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    RefreshCwIcon,
    UploadIcon,
    UploadCloudIcon,
    PlusIcon,
    ExternalLinkIcon,
    ImageIcon,
    ImageOffIcon,
    FileTextIcon,
    BarChart3Icon,
    AlertTriangleIcon,
    CheckCircle2Icon,
    CheckIcon,
    ClipboardCopyIcon,
    Loader2Icon,
} from "lucide-react";
import { Button } from "../primitives/Button";
import { SourceChip } from "../primitives/SourceChip";
import { Chip } from "../primitives/Chip";
import { cn } from "../lib/cn";
import { pageHref } from "../lib/oura";
import type { AuditIssue } from "../audit";
import { CopyTextButton } from "./aeo/CopyTextButton";
import type { AeoPagePublishResult } from "@service/aeo";
import { PublishRefusalNotice } from "../publish/PublishRefusalNotice";
import { Dot } from "../ui";
import { RemediationSection } from "./RemediationSection";
import { ConnectorLogo } from "./connectorLogos";
import { asConnectorKey, connectorLabel } from "../connectors";
import { ASK_OURA_ON } from "../ask/flag";
import { useWritesPaused } from "@context/maintenance";

/**
 * Ask Oura (behind VITE_ENABLE_OURA_ASK): the Answer Plan label on a copy
 * proposal, or null. Everything Ask Oura shows or does in the workshop reads
 * this, so with the switch off the workshop is exactly as on main.
 */
const planOrigin = (r: PageRecommendation): string | null => (ASK_OURA_ON && r.kind === "copy" && r.origin ? r.origin : null);
import {
    getPageWorkshop,
    recsForBlock,
    linksAdded,
    linkBudgetForRole,
    ROLE_LABEL,
    type PageHistoryChange,
    type PageHistoryEntry,
    type PageReview,
    type PageRole,
    type PageBlock,
    type PageLink,
    type PageRecommendation,
    type PageWorkshop,
} from "../pageWorkshop";

const KIND_LABEL: Record<PageRecommendation["kind"], string> = {
    copy: "Copy change",
    link: "Internal link",
    repoint: "Wrong link target",
    related: "Related links row",
};

/**
 * The words a proposal is ABOUT, for a one-line reference to it (the staged-set
 * chips). A recommendation carries no title on the wire — it is anchored to a
 * text span — so the span itself is the only honest name for it. Truncation is
 * left to CSS so the full text can still live in a `title` attribute.
 */
function recAnchorText(rec: PageRecommendation): string {
    switch (rec.kind) {
        case "copy":
            return rec.before;
        case "link":
            return rec.matchedText ?? rec.anchorText;
        case "repoint":
            return rec.anchorText;
        case "related":
            return rec.label;
    }
}

/** Block tag → how its text renders in the document. Editorial scale (the
 *  Profound/Surfer treatment): the document reads like the article it is,
 *  not like a form — mirrors real emphasis without pretending to be the
 *  site's CSS. */
const TAG_TEXT: Record<PageBlock["tag"], string> = {
    h1: "text-2xl font-bold leading-snug text-gray-900",
    h2: "text-lg font-semibold leading-snug text-gray-900",
    h3: "text-base font-semibold leading-snug text-gray-900",
    p: "text-[15px] leading-relaxed text-gray-800",
    li: "text-[15px] leading-relaxed text-gray-800",
    img: "",
    table: "",
    stat: "",
    quote: "",
};

/** Vertical rhythm per tag — headings breathe, body stays tight. */
const TAG_GAP: Record<PageBlock["tag"], string> = {
    h1: "pb-1",
    h2: "pt-4",
    h3: "pt-3",
    p: "",
    li: "",
    img: "py-1",
    table: "py-2",
    stat: "py-1",
    quote: "py-1.5",
};

/** Nudges the gutter tag onto the first text line of taller headings. */
const TAG_GUTTER_PAD: Record<PageBlock["tag"], string> = {
    h1: "pt-2",
    h2: "pt-1.5",
    h3: "pt-1",
    p: "pt-0.5",
    li: "pt-0.5",
    img: "pt-2.5",
    table: "pt-1",
    stat: "pt-2.5",
    quote: "pt-1",
};

/** Gutter label per tag — kept to gutter width ("tbl" not "table"). */
const GUTTER_LABEL: Partial<Record<PageBlock["tag"], string>> = {
    table: "tbl",
    quote: "q",
};

/** '2026-08-02T14:38:00Z' to '2 Aug 2026'. No dashes in visible copy. */
function versionDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** `text` with the anchor substring rendered as the link it would become. */
function withAnchor(text: string, anchor: string) {
    const start = text.indexOf(anchor);
    if (start === -1) return <>{text}</>;
    return (
        <>
            {text.slice(0, start)}
            <span className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2">
                {anchor}
            </span>
            {text.slice(start + anchor.length)}
        </>
    );
}

/**
 * A block's text with the recommended spans highlighted inline.
 *
 * Spans are found by exact substring — the same text-anchoring rule the
 * publish path will use, so what is highlighted here is exactly what a
 * publish would touch. A dismissed proposal's highlight goes gray: still
 * findable, no longer asking for attention.
 */
/**
 * Find `needle` in `haystack`, tolerating the differences that are not
 * differences (#3780).
 *
 * A proposal's "on the page now" text comes back through a model and a
 * serializer; the extracted block comes off the live HTML. They routinely
 * disagree on characters nobody typed differently: a curly apostrophe against
 * a straight one, a non-breaking space, two spaces collapsed to one, an en
 * dash against a hyphen. A plain `indexOf` calls that a miss, and a miss means
 * no highlight is registered at all — which is why the pager scrolled nowhere.
 *
 * NORMALISED FOR THE SEARCH, ORIGINAL FOR THE RESULT. The returned offsets are
 * indices into the ORIGINAL haystack, recovered through a per-character map, so
 * the highlight still lands on the real text with its real punctuation. A
 * normalised offset used directly would drift by one character for every
 * collapsed space and quietly slice the wrong span.
 *
 * Still exact about content: no fuzzy distance, no case folding, no partial
 * match. If the words differ, this reports a miss, because "close to what the
 * page says" is not a claim this screen may make.
 */
function normalizeForMatch(text: string): { norm: string; map: number[] } {
    const map: number[] = [];
    let norm = "";
    let lastWasSpace = false;
    for (let i = 0; i < text.length; i++) {
        let ch = text[i];
        if (ch === "\u2018" || ch === "\u2019" || ch === "\u02BC") ch = "'";
        else if (ch === "\u201C" || ch === "\u201D") ch = '"';
        else if (ch === "\u2013" || ch === "\u2014") ch = "-";
        else if (ch === "\u2026") ch = "...";
        if (/\s/.test(ch)) {
            // A run of any whitespace becomes one space. Skipping the rest is
            // what keeps the map aligned: one normalised char, one source index.
            if (lastWasSpace) continue;
            ch = " ";
            lastWasSpace = true;
        } else {
            lastWasSpace = false;
        }
        for (let k = 0; k < ch.length; k++) map.push(i);
        norm += ch;
    }
    return { norm, map };
}

function findSpan(haystack: string, needle: string): { start: number; end: number } | null {
    const exact = haystack.indexOf(needle);
    if (exact !== -1) return { start: exact, end: exact + needle.length };

    const hay = normalizeForMatch(haystack);
    const want = normalizeForMatch(needle).norm.trim();
    if (!want) return null;
    const at = hay.norm.indexOf(want);
    if (at === -1) return null;
    // Back to original coordinates. `end` is the source index of the last
    // matched character plus one, never `start + want.length` — the normalised
    // length is not the source length once anything was collapsed.
    return { start: hay.map[at], end: hay.map[at + want.length - 1] + 1 };
}

function BlockText({
    block,
    recs,
    selectedId,
    staged,
    onSelect,
    registerMark,
}: {
    block: PageBlock;
    recs: PageRecommendation[];
    selectedId: string | null;
    staged: Set<string>;
    onSelect: (id: string) => void;
    /** Lets the view scroll a highlight into view when the pager moves. */
    registerMark: (id: string, el: HTMLElement | null) => void;
}) {
    // The spans to mark, in text order: proposal spans first (they win any
    // overlap), then the anchors that ALREADY exist on the page — rendered
    // as the links they are, so the reviewer can tell what is there from
    // what is proposed. Insertion-type link recs (requiresNewCopy) have no
    // span in this block — they render as an insertion marker after it,
    // handled by the caller.
    type Span = { start: number; end: number; rec?: PageRecommendation; link?: PageLink };
    const spans: Span[] = [];
    const overlaps = (s: number, e: number) =>
        spans.some((x) => s < x.end && e > x.start);

    recs.forEach((r) => {
        // What to highlight, per kind. `related` has no span (it is a row
        // appended after the block); a `link` whose phrase differs from the
        // live page highlights what is ACTUALLY there, since that is what a
        // publish can touch.
        const needle =
            r.kind === "copy"
                ? r.before
                : r.kind === "related"
                    ? null
                    : r.kind === "repoint"
                        ? r.anchorText
                        : r.requiresNewCopy
                            ? null
                            : r.matchedText ?? r.anchorText;
        if (!needle) return;
        const hit = findSpan(block.text, needle);
        if (!hit || overlaps(hit.start, hit.end)) return;
        spans.push({ start: hit.start, end: hit.end, rec: r });
    });
    (block.links ?? []).forEach((l) => {
        // Same matcher: a link's anchor text comes from the same place and
        // drifts the same way. Fixing it in one helper rather than at each
        // caller is the point.
        const hit = findSpan(block.text, l.text);
        if (!hit || overlaps(hit.start, hit.end)) return;
        spans.push({ start: hit.start, end: hit.end, link: l });
    });
    spans.sort((a, b) => a.start - b.start);

    if (spans.length === 0) return <>{block.text}</>;

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    spans.forEach((m, i) => {
        if (m.start > cursor) parts.push(block.text.slice(cursor, m.start));
        const slice = block.text.slice(m.start, m.end);
        if (m.rec) {
            const active = m.rec.id === selectedId;
            const isStaged = staged.has(m.rec.id);
            // Square edges, always a bottom border: an UNSTAGED proposal is a
            // faint marker-pen wash so it cannot be read as a hyperlink (a
            // plain underline would collide with the page's real links). STAGED
            // fills solid — it is the set about to be written to a live site, so
            // it should be the thing the eye lands on (#3546). Selection adds a
            // ring on top of either, since "what I am reading" and "what is
            // going out" are different questions.
            parts.push(
                <button
                    key={i}
                    ref={(el) => registerMark(m.rec!.id, el)}
                    onClick={() => onSelect(m.rec!.id)}
                    className={cn(
                        "rounded-none border-b-2 text-left transition-colors",
                        isStaged
                            ? active
                                ? "border-blue-700 bg-blue-200"
                                : "border-blue-600 bg-blue-100 hover:bg-blue-200"
                            : active
                                ? "border-blue-400 bg-blue-50"
                                : "border-blue-300 bg-blue-50/40 hover:bg-blue-50",
                    )}
                >
                    {slice}
                </button>,
            );
        } else if (m.link) {
            // An anchor that already exists on the page, shown as the link
            // it is. Absolute destinations open; relative ones (other pages
            // of the client's site) display without navigating.
            const external = /^https?:\/\//i.test(m.link.href);
            parts.push(
                external ? (
                    <a
                        key={i}
                        href={m.link.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={m.link.href}
                        className="text-blue-700 underline decoration-blue-300 underline-offset-2 hover:decoration-blue-600"
                    >
                        {slice}
                    </a>
                ) : (
                    <span
                        key={i}
                        title={`Existing link to ${m.link.href}`}
                        className="text-blue-700 underline decoration-blue-300 underline-offset-2"
                    >
                        {slice}
                    </span>
                ),
            );
        }
        cursor = m.end;
    });
    if (cursor < block.text.length) parts.push(block.text.slice(cursor));
    return <>{parts}</>;
}

/**
 * One step of the proposal, as a numbered stage rather than a boxed field.
 *
 * The card used to nest a bordered box inside a bordered card inside a
 * bordered pane — three frames around one sentence, which read as clutter
 * pretending to be structure. A step is now just: a number, a label, and the
 * content, held together by a single hairline rail down the left. The rail
 * turns blue on the final step, because that is the one thing on the screen
 * that does not exist on the page yet.
 */
function Step({
    n,
    label,
    accent,
    last,
    action,
    children,
}: {
    n: number;
    label: string;
    accent?: boolean;
    /** The rail stops at the last step — it joins steps, it is not a border. */
    last?: boolean;
    /** Sits on the label row, right-aligned — an action ABOUT this step. */
    action?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <div className="relative pl-7">
            <span
                className={cn(
                    "absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                    accent ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500",
                )}
            >
                {n}
            </span>
            {/* The rail joins the steps into one sequence — the hierarchy the
                boxes were failing to communicate. */}
            {!last && (
                <span className="absolute -bottom-4 left-[9px] top-6 w-px bg-gray-200" />
            )}
            <div className="mb-1 flex items-baseline justify-between gap-3">
                <p className={cn("text-xs font-medium", accent ? "text-blue-700" : "text-gray-500")}>
                    {label}
                </p>
                {action}
            </div>
            <div className="text-[15px] leading-relaxed text-gray-900">{children}</div>
        </div>
    );
}

/** Page copy, quoted. Steps 1 and 3 hold text FROM the page; step 2 is us
 *  talking about it. Same typeface throughout, so the quotation marks are
 *  what tells the reviewer which is which. */
function Quoted({ children }: { children: React.ReactNode }) {
    return (
        <>
            <span aria-hidden className="text-gray-400">“</span>
            {children}
            <span aria-hidden className="text-gray-400">”</span>
        </>
    );
}

function RecommendationCard({
    rec,
    blockText,
    staged,
    note,
    onNote,
    onOpenPage,
    washed,
    ouraSuggests,
}: {
    rec: PageRecommendation;
    /** The current text of the block this proposal touches. */
    blockText: string;
    /** Proposals are IN by default; this is the reviewer's opt-out. */
    staged: boolean;
    note: string;
    onNote: (text: string) => void;
    /** Jump to another page's workshop (the other half of a two-way link). */
    onOpenPage?: (url: string, title: string) => void;
    /** PREVIEW (Ask Oura): briefly marked, it arrived from the Answer Plan. */
    washed?: boolean;
    /** PREVIEW (Ask Oura): Oura's own proposal on a page that also holds the client's approved answers. */
    ouraSuggests?: boolean;
}) {
    const [noteOpen, setNoteOpen] = useState(note.length > 0);
    // Ask Oura: the Answer Plan label, null with the switch off.
    const origin = planOrigin(rec);

    /** "Links to X · /url" — one line, under whichever text carries it. */
    const destination = (title: string, url: string) => (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
            <LinkIcon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate" title={url}>
                Links to {title} · {url}
            </span>
        </p>
    );

    // The words that actually get linked. When the generator's phrase differs
    // from the live page, the LIVE text is what a publish can touch.
    const liveAnchor =
        rec.kind === "link" ? rec.matchedText ?? rec.anchorText : rec.kind === "repoint" ? rec.anchorText : "";

    // Step 1 differs by kind: a repoint's "now" is a DESTINATION, not text.
    const stepOne =
        rec.kind === "copy" ? (
            <span className="text-gray-600">
                <Quoted>{rec.before}</Quoted>
            </span>
        ) : rec.kind === "repoint" ? (
            <span className="text-gray-600">
                <Quoted>{rec.anchorText}</Quoted> currently links to{" "}
                <span className="font-medium text-gray-700">{rec.currentUrl}</span>
            </span>
        ) : (
            <span className="text-gray-600">
                <Quoted>{blockText}</Quoted>
            </span>
        );

    const stepThreeLabel =
        rec.kind === "copy"
            ? "Proposed wording"
            : rec.kind === "repoint"
                ? "Proposed destination"
                : rec.kind === "related"
                    ? `Proposed, a ${rec.label.toLowerCase()} row`
                    : rec.requiresNewCopy
                        ? "Proposed, one new sentence"
                        : "Proposed, this text becomes a link";

    return (
        <div
            className={ASK_OURA_ON ? cn(washed && "-m-2 rounded-xl p-2 animate-[askPlanWash_1600ms_cubic-bezier(0.2,0.8,0.2,1)_both] motion-reduce:animate-none") : undefined}
            data-rec-card={ASK_OURA_ON ? rec.id : undefined}
        >
            {origin && (
                <p className="mb-0.5 flex items-center gap-1 text-xs font-medium text-blue-700" data-rec-origin>
                    <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    {origin}
                </p>
            )}
            {ASK_OURA_ON && ouraSuggests && !origin && (
                <p className="mb-0.5 text-xs font-medium text-gray-500" data-rec-oura-suggests>
                    Oura suggests · not yet approved by the client
                </p>
            )}
            <p className="mb-3 text-xs font-medium text-gray-500">{KIND_LABEL[rec.kind]}</p>

            {/* THE ORDER IS THE ARGUMENT: what the page says now, why that is
                a problem, then what to say instead. Reading the proposed text
                before the reason invites judging prose on taste; reading the
                reason first makes the new text an answer to a stated
                problem. */}
            <div className="space-y-4">
                <Step n={1} label="On the page now">{stepOne}</Step>

                <Step n={2} label="Why it needs to change">
                    <span className="text-gray-700">
                        {rec.justification.reason} {rec.justification.expected}
                    </span>
                    {/* Where the facts in the proposal came from (#3756). A chip
                        here is how a reviewer knows a price came from the
                        client's own price list rather than from a model's
                        imagination — without it the copy simply looks more
                        confident. Empty renders nothing: most businesses have no
                        documents, and an absent source is the honest showing. */}
                    {rec.citations && rec.citations.length > 0 && (
                        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-gray-400">From your documents:</span>
                            <SourceChip citations={rec.citations} />
                        </span>
                    )}
                </Step>

                <Step
                    n={3}
                    accent
                    last
                    label={stepThreeLabel}
                    action={
                        <span className="flex flex-shrink-0 items-center gap-3">
                            {/* Always offered, not only where publishing is refused.
                                Hiding it on publishable pages would make it a mode
                                the reviewer has to notice, and it is useful
                                anywhere — the proposed wording sits inside a diff,
                                which is the one place it is awkward to select. */}
                            {rec.kind === "copy" && <CopyTextButton text={rec.after} />}
                            {!noteOpen && (
                                <button
                                    onClick={() => setNoteOpen(true)}
                                    className="flex-shrink-0 text-xs font-medium text-gray-500 underline-offset-4 transition-colors hover:text-gray-800 hover:underline"
                                >
                                    Ask for a different take
                                </button>
                            )}
                        </span>
                    }
                >
                    {rec.kind === "copy" ? (
                        <Quoted>{rec.after}</Quoted>
                    ) : rec.kind === "repoint" ? (
                        <>
                            <span className="text-gray-900">
                                Should link to{" "}
                                <span className="font-medium text-blue-700">{rec.targetUrl}</span>
                            </span>
                            {destination(rec.targetTitle, rec.targetUrl)}
                        </>
                    ) : rec.kind === "related" ? (
                        // A ROW OF LINKS, not rewritten copy — the build sheet
                        // is explicit that these pages get a small related row
                        // rather than a new sentence.
                        <div className="rounded-md border border-blue-200 bg-blue-50/40 px-3 py-2">
                            <p className="my-0 mb-1 text-xs font-semibold text-blue-700">{rec.label}</p>
                            <ul className="my-0 space-y-0.5 pl-0">
                                {rec.links.map((l) => (
                                    <li key={l.url} className="flex items-center gap-1.5 text-sm text-gray-700">
                                        <LinkIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                        <span className="truncate" title={l.url}>
                                            {l.title} · <span className="text-gray-500">{l.url}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : rec.requiresNewCopy && rec.proposedCopy ? (
                        <>
                            <Quoted>
                                <span className="text-gray-500">{blockText} </span>
                                {withAnchor(rec.proposedCopy, rec.anchorText)}
                            </Quoted>
                            {destination(rec.targetTitle, rec.targetUrl)}
                        </>
                    ) : (
                        <>
                            <Quoted>{withAnchor(blockText, liveAnchor)}</Quoted>
                            {destination(rec.targetTitle, rec.targetUrl)}
                        </>
                    )}

                    {/* The phrase did not match the live page word for word.
                        Silently linking a different string is how a publish
                        goes wrong, so it is stated. */}
                    {rec.kind === "link" && rec.match === "approximate" && (
                        <p className="mt-1.5 text-xs text-amber-700">
                            This phrase isn't on the page as written. Closest match:{" "}
                            <span className="font-medium">“{rec.matchedText}”</span> — that is what
                            gets linked.
                        </p>
                    )}
                    {rec.kind === "link" && rec.match === "missing" && (
                        <p className="mt-1.5 text-xs text-amber-700">
                            Phrase not found on this page. Nothing can be linked until the wording
                            is confirmed.
                        </p>
                    )}

                    {/* The other half of a two-way link. Informational only —
                        it is an ordinary proposal on that page, approved and
                        published there. */}
                    {rec.kind === "link" && rec.pairsWith && (
                        <p className="mt-1.5 text-xs text-gray-500">
                            Return link proposed on{" "}
                            <button
                                onClick={() => onOpenPage?.(rec.pairsWith!.url, rec.pairsWith!.title)}
                                className="font-medium text-blue-600 underline-offset-2 hover:underline"
                            >
                                {rec.pairsWith.url}
                            </button>
                        </p>
                    )}
                </Step>
            </div>

            {/* NOTHING IS STAGED BY DEFAULT (#3546), so the state worth showing
                is now the exception in the other direction: this one is going
                out. The staging control itself lives on the proposal's title
                row. What remains here is the note — it only exists once she
                asks for one. */}
            {staged && (
                <p className="mt-4 flex items-center gap-1.5 border-t border-gray-100 pt-3 text-xs font-medium text-blue-700">
                    <CheckIcon className="h-3.5 w-3.5" />
                    Added, this one publishes with the next Publish
                </p>
            )}
            {noteOpen && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                    <textarea
                        value={note}
                        onChange={(e) => onNote(e.target.value)}
                        rows={2}
                        maxLength={500}
                        autoFocus
                        placeholder="What should the next draft of this proposal do differently?"
                        className="w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1.5 text-xs text-gray-400">
                        Kept until you redraft. Note as many proposals as you like, then redraft
                        once.
                    </p>
                </div>
            )}
        </div>
    );
}

const PLAN_KEYFRAMES =
    "@keyframes askPlanWash { 0% { background-color: rgba(37, 99, 235, 0.12); } 70% { background-color: rgba(37, 99, 235, 0.06); } 100% { background-color: transparent; } }";

/**
 * The margin marker for a block that carries proposals — the at-a-glance
 * answer to "what kind of change lives here": a doc icon for copy, a link
 * icon for internal links, and a +N badge when one span carries several.
 * Clicking cycles through the block's proposals.
 */
function BlockMarker({
    recs,
    selectedId,
    staged,
    onSelect,
}: {
    recs: PageRecommendation[];
    selectedId: string | null;
    staged: Set<string>;
    onSelect: (id: string) => void;
}) {
    if (recs.length === 0) return null;
    const noneStaged = recs.every((r) => !staged.has(r.id));
    const activeInBlock = recs.some((r) => r.id === selectedId);
    const Icon = recs[0].kind === "copy" ? FileTextIcon : LinkIcon;
    const extra = recs.length - 1;
    const click = () => {
        const idx = recs.findIndex((r) => r.id === selectedId);
        onSelect(recs[(idx + 1) % recs.length].id);
    };
    return (
        <button
            onClick={click}
            aria-label={`${recs.length} ${recs.length === 1 ? "proposal" : "proposals"} for this block`}
            className={cn(
                "relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border bg-white shadow-sm transition-colors",
                activeInBlock
                    ? "border-blue-500 ring-1 ring-blue-500"
                    : noneStaged
                        ? "border-gray-200 hover:border-blue-400"
                        : "border-blue-500",
            )}
        >
            <Icon className={cn("h-3.5 w-3.5", noneStaged ? "text-gray-400" : "text-blue-600")} />
            {extra > 0 && (
                <span
                    className={cn(
                        "absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-0.5 text-[9px] font-semibold leading-none text-white",
                        noneStaged ? "bg-gray-400" : "bg-blue-600",
                    )}
                >
                    +{extra}
                </span>
            )}
        </button>
    );
}

/** A crawled image. When the crawler captured a real `src` the actual image
 *  renders (with its alt as caption); a load failure degrades to a labelled
 *  placeholder instead of a broken-image glyph. Charts get a chart icon —
 *  their pixels carry no extractable copy, only the alt text.
 *
 *  The two placeholder causes are DIFFERENT findings and say so: "failed to
 *  load" is a live image the browser could not fetch right now (worth a second
 *  look — it may be broken on the site too), while "not captured" means the
 *  crawl never had a URL for it and there is nothing to retry. One identical
 *  grey strip for both left the reviewer guessing which problem they had. */
function ImgBlock({ block }: { block: PageBlock }) {
    const [broken, setBroken] = useState(false);
    const isChart = block.figure === "chart";
    if (block.src && !broken) {
        return (
            <figure className="my-0 mx-0">
                <img
                    src={block.src}
                    alt={block.alt ?? ""}
                    onError={() => setBroken(true)}
                    className="max-h-64 w-full rounded-md border border-gray-200 object-cover"
                />
                {block.alt && (
                    <figcaption className="mt-1 text-xs text-gray-400">{block.alt}</figcaption>
                )}
            </figure>
        );
    }
    const Icon = isChart ? BarChart3Icon : broken ? ImageOffIcon : ImageIcon;
    return (
        <div
            className={cn(
                "flex items-center gap-2 rounded-md border border-dashed px-3 py-2.5",
                broken ? "border-amber-300 bg-amber-50/50" : "border-gray-300 bg-gray-50",
            )}
        >
            <Icon className={cn("h-4 w-4 flex-shrink-0", broken ? "text-amber-500" : "text-gray-400")} />
            <span className="min-w-0 truncate text-xs text-gray-500">
                {block.alt || (isChart ? "Chart" : "Image")}
                <span className={cn(broken ? "text-amber-700" : "text-gray-400")}>
                    {" "}· {broken ? "the live image failed to load" : "no image captured by the crawl"}
                </span>
            </span>
        </div>
    );
}

/** A table, verbatim — fee schedules, opening hours, comparison grids. */
function TableBlock({ block }: { block: PageBlock }) {
    if (!block.table) return null;
    return (
        <div>
            {block.text && (
                <p className="mb-1.5 mt-0 text-sm font-semibold text-gray-900">{block.text}</p>
            )}
            <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-gray-50">
                            {block.table.header.map((hd, i) => (
                                <th key={i} className="px-3 py-1.5 text-left text-xs font-medium text-gray-500">
                                    {hd}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {block.table.rows.map((row, i) => (
                            <tr key={i}>
                                {row.map((cell, j) => (
                                    <td key={j} className="px-3 py-1.5 text-gray-700">{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/** A pull-quote / testimonial, with its attribution. */
function QuoteBlock({ block }: { block: PageBlock }) {
    return (
        <blockquote className="my-0 border-l-2 border-gray-300 pl-3">
            <p className="my-0 text-[15px] italic leading-relaxed text-gray-700">
                "{block.text}"
            </p>
            {block.label && <footer className="mt-1 text-xs text-gray-500">{block.label}</footer>}
        </blockquote>
    );
}

/** Blocks in document order, with consecutive `group` blocks folded into one
 *  visual unit (a card grid, a stat row) and consecutive `li` blocks folded
 *  into one real list — the way they appear on the page. */
type DocUnit =
    | { kind: "block"; block: PageBlock }
    | { kind: "cluster"; group: string; blocks: PageBlock[] }
    | { kind: "list"; blocks: PageBlock[] };

/**
 * Does this block put anything on the page?
 *
 * Extraction on a page-builder site (Wix, Squarespace) yields layout wrappers
 * that carry no content of their own — they exist to hold a button or an icon
 * the extractor cannot represent. Rendered, they are blank boxes: the reviewer
 * reads them as "Oura lost something here", and on a card grid they crowd out
 * the cards that DO have content.
 *
 * Dropping them is presentation only — nothing anchors to a block with no text,
 * so no proposal can be pointing at one.
 */
function hasContent(b: PageBlock): boolean {
    return (
        b.text.trim().length > 0 ||
        !!b.src ||
        !!b.alt?.trim() ||
        !!b.label?.trim() ||
        !!b.table ||
        (b.links?.length ?? 0) > 0
    );
}

function clusterBlocks(blocks: PageBlock[]): DocUnit[] {
    const units: DocUnit[] = [];
    for (const b of blocks.filter(hasContent)) {
        const last = units[units.length - 1];
        if (b.group) {
            if (last && last.kind === "cluster" && last.group === b.group) last.blocks.push(b);
            else units.push({ kind: "cluster", group: b.group, blocks: [b] });
        } else if (b.tag === "li") {
            // A run of `li` blocks IS a list — rendered as a real <ul> so the
            // document keeps list semantics (indentation, screen-reader
            // announcement of item count) instead of paragraphs wearing a
            // literal bullet character. A lone `li` still gets a one-item
            // list: that is what it is on the page.
            if (last && last.kind === "list") last.blocks.push(b);
            else units.push({ kind: "list", blocks: [b] });
        } else {
            units.push({ kind: "block", block: b });
        }
    }
    return units;
}

/** The whole extracted page: gutter of tags, real images, tables, stat rows,
 *  card grids, existing links — and insertion markers where a proposed
 *  sentence would be added. Real pages are messy; the pane renders the mess
 *  faithfully so the reviewer judges changes in the page's true shape. */
function DocumentPane({
    page,
    selectedId,
    staged,
    onSelect,
    registerMark,
}: {
    page: PageWorkshop;
    selectedId: string | null;
    staged: Set<string>;
    onSelect: (id: string) => void;
    registerMark: (id: string, el: HTMLElement | null) => void;
}) {
    const textProps = { selectedId, staged, onSelect, registerMark };

    const marker = (recs: PageRecommendation[]) => (
        <div className="flex w-8 flex-shrink-0 justify-center pt-0.5">
            <BlockMarker recs={recs} selectedId={selectedId} staged={staged} onSelect={onSelect} />
        </div>
    );

    // Insertion-type recs render a marker AFTER their unit: the proposed
    // sentence does not exist on the page yet, so there is no span to
    // highlight — only a place it would go.
    const insertionRows = (blocks: PageBlock[]) =>
        blocks
            .flatMap((b) => recsForBlock(page, b.id))
            .filter(
                (
                    r,
                ): r is
                    | Extract<PageRecommendation, { kind: "link" }>
                    | Extract<PageRecommendation, { kind: "related" }> =>
                    (r.kind === "link" && r.requiresNewCopy) || r.kind === "related",
            )
            .map((r) => {
                const isStaged = staged.has(r.id);
                const label =
                    r.kind === "related"
                        ? `Oura proposes a ${r.label} row here, with ${r.links.length} links`
                        : `Oura proposes a new sentence here, to carry a link to ${r.targetUrl}`;
                return (
                    <div key={r.id} className="flex gap-3 px-1 py-0.5">
                        <span className="w-7 flex-shrink-0" />
                        <button
                            ref={(el) => registerMark(r.id, el)}
                            onClick={() => onSelect(r.id)}
                            className={cn(
                                "flex min-w-0 flex-1 items-center gap-2 rounded-md border border-dashed px-3 py-1.5 text-left transition-colors",
                                isStaged
                                    ? r.id === selectedId
                                        ? "border-blue-600 bg-blue-100 ring-1 ring-blue-600"
                                        : "border-blue-500 bg-blue-100 hover:bg-blue-200"
                                    : r.id === selectedId
                                        ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400"
                                        : "border-blue-200 bg-blue-50/40 hover:bg-blue-50",
                            )}
                        >
                            <PlusIcon
                                className={cn(
                                    "h-3.5 w-3.5 flex-shrink-0",
                                    isStaged ? "text-blue-700" : "text-blue-400",
                                )}
                            />
                            <span className={cn("truncate text-xs", isStaged ? "font-medium text-blue-800" : "text-blue-600")}>
                                {label}
                            </span>
                        </button>
                        <span className="w-8 flex-shrink-0" />
                    </div>
                );
            });

    return (
        <div className="space-y-1">
            {clusterBlocks(page.blocks).map((unit) => {
                if (unit.kind === "cluster") {
                    const recs = unit.blocks.flatMap((b) => recsForBlock(page, b.id));
                    const isStats = unit.group.startsWith("stats");
                    // Card grids split at each h3 — the same repeated
                    // sibling structure the DOM walk detects them by.
                    const cards: PageBlock[][] = [];
                    if (!isStats) {
                        unit.blocks.forEach((b) => {
                            if (b.tag === "h3" || cards.length === 0) cards.push([]);
                            cards[cards.length - 1].push(b);
                        });
                    }
                    return (
                        <div key={unit.blocks[0].id} className="py-1.5">
                            <div className="flex gap-3 rounded px-1 py-0.5">
                                <span className="w-7 flex-shrink-0 pt-2 text-center font-mono text-[11px] lowercase leading-none text-gray-400">
                                    {isStats ? "stat" : "card"}
                                </span>
                                {isStats ? (
                                    <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                                        {unit.blocks.map((b) => (
                                            <div
                                                key={b.id}
                                                className="min-w-[110px] flex-1 rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2 text-center"
                                            >
                                                <div className="text-xl font-semibold leading-tight text-gray-900 tabular-nums">
                                                    {b.text}
                                                </div>
                                                <div className="mt-0.5 text-xs text-gray-500">{b.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="grid min-w-0 flex-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                                        {cards.map((cb) => (
                                            <div key={cb[0].id} className="rounded-lg border border-gray-200 bg-white p-3">
                                                {cb.map((b) =>
                                                    b.tag === "h3" ? (
                                                        // A card's title is a heading on the
                                                        // page — keep it one here.
                                                        <h3 key={b.id} className="mb-1 mt-0 text-sm font-semibold text-gray-900">
                                                            <BlockText block={b} recs={recsForBlock(page, b.id)} {...textProps} />
                                                        </h3>
                                                    ) : (
                                                        <p key={b.id} className="my-0 text-[13px] leading-relaxed text-gray-600">
                                                            <BlockText block={b} recs={recsForBlock(page, b.id)} {...textProps} />
                                                        </p>
                                                    ),
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {marker(recs)}
                            </div>
                            {insertionRows(unit.blocks)}
                        </div>
                    );
                }

                if (unit.kind === "list") {
                    const recs = unit.blocks.flatMap((b) => recsForBlock(page, b.id));
                    return (
                        <div key={unit.blocks[0].id}>
                            <div className="flex gap-3 rounded px-1 py-0.5">
                                <span
                                    className={cn(
                                        "w-7 flex-shrink-0 text-center font-mono text-[11px] lowercase leading-none text-gray-400",
                                        TAG_GUTTER_PAD.li,
                                    )}
                                >
                                    li
                                </span>
                                {/* A real list: browser indentation, real
                                    markers, and a screen reader announcing
                                    "list, N items" — where a stack of <p>s
                                    with a literal • announced nothing. The
                                    margin marker aggregates the items'
                                    proposals, same as a card cluster's. */}
                                <ul className="my-0 min-w-0 flex-1 list-disc space-y-1 pl-5 marker:text-gray-400">
                                    {unit.blocks.map((b) => (
                                        <li key={b.id} className={TAG_TEXT.li}>
                                            <BlockText
                                                block={b}
                                                recs={recsForBlock(page, b.id)}
                                                {...textProps}
                                            />
                                        </li>
                                    ))}
                                </ul>
                                {marker(recs)}
                            </div>
                            {insertionRows(unit.blocks)}
                        </div>
                    );
                }

                const block = unit.block;
                const recs = recsForBlock(page, block.id);
                return (
                    <div key={block.id} className={TAG_GAP[block.tag]}>
                        <div className="flex gap-3 rounded px-1 py-0.5">
                            {/* The gutter: what this block IS, straight off the
                                DOM. Same treatment as Profound/Surfer — the
                                schema is information the reviewer uses, not
                                decoration. Lowercase and centered, like a
                                margin annotation, not a shouted label. */}
                            <span
                                className={cn(
                                    "w-7 flex-shrink-0 text-center font-mono text-[11px] lowercase leading-none text-gray-400",
                                    TAG_GUTTER_PAD[block.tag],
                                )}
                            >
                                {GUTTER_LABEL[block.tag] ?? block.tag}
                            </span>
                            <div className="min-w-0 flex-1">
                                {block.tag === "img" ? (
                                    <ImgBlock block={block} />
                                ) : block.tag === "table" ? (
                                    <TableBlock block={block} />
                                ) : block.tag === "quote" ? (
                                    <QuoteBlock block={block} />
                                ) : block.tag === "h1" || block.tag === "h2" || block.tag === "h3" ? (
                                    // A REAL heading element, not a styled <p>:
                                    // the extracted document keeps its outline,
                                    // so a screen reader can walk it by heading
                                    // the way a sighted reviewer scans it. The
                                    // visual scale is unchanged — TAG_TEXT was
                                    // already mirroring the page's emphasis.
                                    (() => {
                                        const H = block.tag as "h1" | "h2" | "h3";
                                        return (
                                            <H className={cn("my-0", TAG_TEXT[block.tag])}>
                                                <BlockText block={block} recs={recs} {...textProps} />
                                            </H>
                                        );
                                    })()
                                ) : (
                                    <p className={cn("my-0", TAG_TEXT[block.tag])}>
                                        <BlockText block={block} recs={recs} {...textProps} />
                                    </p>
                                )}
                            </div>
                            {/* The right margin: what KIND of change sits on
                                this line, visible before reading anything.
                                The column is always rendered so text wraps at
                                the same width on every row. */}
                            {marker(recs)}
                        </div>
                        {insertionRows([block])}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * One recorded change: where it was, and what it did. Copy changes show the
 * wording both ways (struck through, then the replacement) because "reworded
 * the opening line" is not something anyone can audit.
 */
function ChangeRow({ change }: { change: PageHistoryChange }) {
    const isCopy = change.kind === "copy";
    return (
        <li className="text-xs leading-relaxed">
            <span className="flex items-center gap-1.5 text-gray-400">
                {isCopy ? (
                    <FileTextIcon className="h-3 w-3 flex-shrink-0" />
                ) : (
                    <LinkIcon className="h-3 w-3 flex-shrink-0" />
                )}
                {change.where}
            </span>
            {isCopy ? (
                <span className="mt-0.5 block">
                    <span className="text-gray-400 line-through decoration-gray-300">
                        {change.before}
                    </span>
                    <span className="mt-0.5 block text-gray-700">{change.after}</span>
                </span>
            ) : change.kind === "related" ? (
                <span className="mt-0.5 block text-gray-700">
                    Related links row added, pointing to {change.targetUrl}
                </span>
            ) : change.kind === "repoint" ? (
                <span className="mt-0.5 block text-gray-700">
                    “{change.anchorText}” repointed from {change.fromUrl} to {change.targetUrl}
                </span>
            ) : (
                <span className="mt-0.5 block text-gray-700">
                    “{change.anchorText}” linked to {change.targetUrl}
                </span>
            )}
        </li>
    );
}

/** Local-day key, so the header matches the times shown under it. */
function dayKey(iso: string): string {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
    ).padStart(2, "0")}`;
}

function timeOfDay(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Icon, label and wording per event kind — an icon says what happened at a
 *  glance, where three colours of dot only said "something did". */
const HISTORY_KIND: Record<
    PageHistoryEntry["kind"],
    { icon: typeof SparklesIcon; label: string }
> = {
    drafted: { icon: SparklesIcon, label: "Draft" },
    redrafted: { icon: RefreshCwIcon, label: "Redraft" },
    published: { icon: UploadIcon, label: "Published" },
};

/** What a change is CALLED in a summary line. `related` folds into "internal
 *  link" on purpose — the distinction is how Oura built it, not what the
 *  client's page got. */
const CHANGE_NOUN: Record<PageHistoryChange["kind"], { one: string; many: string }> = {
    copy: { one: "copy fix", many: "copy fixes" },
    link: { one: "internal link", many: "internal links" },
    related: { one: "internal link", many: "internal links" },
    repoint: { one: "link repointed", many: "links repointed" },
};

/**
 * "1 copy fix" / "2 internal links" / "3 changes" + "(2 internal links, 1 copy fix)".
 *
 * Every entry used to read "N change(s)", so the only way to learn whether a
 * publication rewrote a sentence or added a link was to expand it and read the
 * icons. The kind is already on the wire; this puts it on the line (#3547).
 */
function changeSummary(changes: PageHistoryChange[]): { head: string; breakdown: string } {
    const n = changes.length;
    const plural = n === 1 ? "change" : "changes";
    if (n === 0) return { head: `0 ${plural}`, breakdown: "" };

    const buckets = new Map<string, { one: string; many: string; n: number }>();
    for (const c of changes) {
        // An unrecognised kind from the wire names itself rather than being
        // dropped from the count — the same defensiveness HISTORY_KIND needs.
        const noun = CHANGE_NOUN[c.kind] ?? { one: c.kind, many: `${c.kind} changes` };
        const seen = buckets.get(noun.one);
        if (seen) seen.n += 1;
        else buckets.set(noun.one, { ...noun, n: 1 });
    }

    const parts = Array.from(buckets.values()).sort((a, b) => b.n - a.n);
    const phrase = (p: { one: string; many: string; n: number }) =>
        `${p.n} ${p.n === 1 ? p.one : p.many}`;

    if (parts.length === 1) return { head: phrase(parts[0]), breakdown: "" };
    return { head: `${n} ${plural}`, breakdown: `(${parts.map(phrase).join(", ")})` };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Who did it — or nothing, never a raw id.
 *
 * The BE sends `approved_by` unresolved, so the actor line was printing
 * `10:03 · 07aae17a-7a25-40e9-a713-45283705810e` (#3547; resolution is
 * ourteam-backend#4621). An id identifies nobody to the person reading the
 * log, and it crowds out the timestamp, which does. A draft is Oura's own
 * work, so naming it is accurate; a publication was approved by a person whose
 * name we don't have, and inventing one would be worse than showing none.
 */
function historyActor(e: PageHistoryEntry): string {
    const by = (e.by ?? "").trim();
    if (!by) return "";
    if (!UUID_RE.test(by)) return by;
    return e.kind === "published" ? "" : "Oura";
}

/**
 * What happened to this page, newest first, grouped by day.
 *
 * Same shape as the recruiter Activity Log (collapsible date header, icon
 * tile, time and actor under the line) because it answers the same question
 * and the team already reads that pattern. Publications sit in this list
 * rather than a separate one: a history that showed four drafts and never said
 * the copy went live would not explain why the page reads as it does.
 */
function HistoryTimeline({ entries }: { entries: PageHistoryEntry[] }) {
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const [shown, setShown] = useState<Record<string, boolean>>({});

    const days = useMemo(() => {
        const byDay = new Map<string, PageHistoryEntry[]>();
        for (const e of entries) {
            const k = dayKey(e.at);
            const bucket = byDay.get(k);
            if (bucket) bucket.push(e);
            else byDay.set(k, [e]);
        }
        return Array.from(byDay.entries())
            .map(([day, list]) => ({
                day,
                entries: list
                    .slice()
                    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()),
            }))
            .sort((a, b) => b.day.localeCompare(a.day));
    }, [entries]);

    if (entries.length === 0) {
        return (
            <p className="text-sm text-gray-500">
                Nothing yet. Drafts and published changes will appear here.
            </p>
        );
    }

    return (
        <div className="space-y-5">
            {days.map(({ day, entries: dayEntries }) => {
                const shut = collapsed[day];
                return (
                    <div key={day} className="space-y-3">
                        <button
                            onClick={() => setCollapsed((prev) => ({ ...prev, [day]: !prev[day] }))}
                            className="-mx-2 flex w-full items-center gap-2 rounded-md px-2 py-1 text-left transition-colors hover:bg-gray-50"
                        >
                            {shut ? (
                                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                            ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                            )}
                            <span className="text-sm font-semibold text-gray-700">
                                {versionDate(dayEntries[0].at)}
                            </span>
                            <span className="text-xs text-gray-400">
                                ({dayEntries.length} {dayEntries.length === 1 ? "action" : "actions"})
                            </span>
                        </button>
                        {!shut && (
                            <div className="space-y-4 pl-6">
                                {dayEntries.map((e) => {
                                    // HISTORY_KIND has exactly three keys, and `kind` is a
                                    // hand-written FE union that nothing validates at runtime.
                                    // A fourth kind from the BE (a revert, a failed publish, a
                                    // new event type) made this destructure throw and took the
                                    // whole page white (#3513). An unknown kind now shows its
                                    // raw value, which is more useful than a crash and says
                                    // exactly what to add here.
                                    const { icon: Icon, label } = HISTORY_KIND[e.kind]
                                        ?? { icon: FileTextIcon, label: e.kind };
                                    const open = !!shown[e.id];
                                    const n = e.changes.length;
                                    const published = e.kind === "published";
                                    const summary = changeSummary(e.changes);
                                    const viaKey = asConnectorKey(e.via);
                                    const via = connectorLabel(e.via);
                                    const actor = historyActor(e);
                                    return (
                                        <div key={e.id} className="flex items-start gap-3">
                                            {/* A publication wears the mark of the
                                                platform it wrote to. Three grey
                                                tiles made the one event that
                                                reached the outside world look like
                                                the two that stayed in Oura. */}
                                            <div
                                                className={cn(
                                                    "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md",
                                                    published ? "bg-emerald-50" : "bg-gray-100",
                                                )}
                                            >
                                                {published && viaKey ? (
                                                    <ConnectorLogo k={viaKey} className="h-4 w-4" />
                                                ) : (
                                                    <Icon
                                                        className={cn(
                                                            "h-4 w-4",
                                                            published ? "text-emerald-700" : "text-gray-600",
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-start justify-between gap-3">
                                                    <p className="my-0 text-sm text-gray-900">
                                                        {published ? (
                                                            <>
                                                                {summary.head} published
                                                                {via && ` to ${via}`}
                                                                {summary.breakdown && (
                                                                    <span className="text-gray-500">
                                                                        {" "}
                                                                        {summary.breakdown}
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : e.kind === "redrafted" ? (
                                                            <>Redrafted, {n} proposals</>
                                                        ) : (
                                                            <>Drafted from the scan, {n} proposals</>
                                                        )}
                                                    </p>
                                                    <Chip
                                                        className={cn(
                                                            "flex-shrink-0",
                                                            published
                                                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                                                : "border-gray-200 bg-gray-50 text-gray-600",
                                                        )}
                                                    >
                                                        {label}
                                                    </Chip>
                                                </div>
                                                <p className="my-0 mt-0.5 text-xs text-gray-500">
                                                    {timeOfDay(e.at)}
                                                    {actor && ` · ${actor}`}
                                                </p>
                                                {e.instruction && (
                                                    <p className="my-0 mt-1 text-sm italic leading-relaxed text-gray-600">
                                                        “{e.instruction}”
                                                    </p>
                                                )}
                                                {n > 0 && (
                                                    <>
                                                        <button
                                                            onClick={() =>
                                                                setShown((prev) => ({
                                                                    ...prev,
                                                                    [e.id]: !prev[e.id],
                                                                }))
                                                            }
                                                            className="mt-1 text-xs font-medium text-gray-500 underline-offset-4 transition-colors hover:text-gray-800 hover:underline"
                                                        >
                                                            {open ? "Hide" : "Show"} what changed
                                                        </button>
                                                        {open && (
                                                            <ul className="my-0 mt-1.5 list-plain space-y-2 border-l-2 border-gray-100 pl-3">
                                                                {e.changes.map((c, i) => (
                                                                    <ChangeRow key={i} change={c} />
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Everything the site audit found wrong with THIS page, each with its own fix.
 *
 * The argument this settles: the Crawled Pages table sends you to a workshop
 * that could only ever fix two things (copy and internal links), while the rest
 * of the same page's problems lived on the Site Audit tab in a drawer organised
 * by defect. So "work this page" meant working two surfaces and holding the page
 * in your head across both.
 *
 * Nothing new is generated to make this work. `RemediationSection` is already
 * scoped to exactly (business, issue, page) and already offers only the draft
 * types that can resolve a given finding — it is the same component the Issues
 * drawer renders per page. Here it is simply given this page and each of its
 * findings in turn.
 *
 * Deliberately collapsed by default. A page with five findings would otherwise
 * open five draft generators at once, and the point is to pick one and do it.
 */
function PageAuditPanel({
    issues,
    pageUrl,
    businessId,
    publishUnsupported,
}: {
    issues?: AuditIssue[];
    pageUrl?: string;
    businessId?: string;
    /** See DraftRow — threaded through unchanged (#3692). */
    publishUnsupported?: (draftType: string) => string | undefined;
}) {
    const [openKey, setOpenKey] = useState<string | null>(null);

    // Not measured, and said so. An empty list here would claim a clean page.
    if (!issues) {
        return (
            <div className="py-12 text-center">
                <p className="my-0 text-sm font-medium text-gray-900">No site audit for this scan</p>
                <p className="mx-auto my-0 mt-2 max-w-xs text-sm leading-relaxed text-gray-500">
                    This assessment did not report a crawl inventory, so there is nothing to say
                    about this page's checks. The next scan will carry one.
                </p>
            </div>
        );
    }

    // Measured, and clean. A real result, and worth stating plainly.
    if (issues.length === 0) {
        return (
            <div className="py-12 text-center">
                <CheckCircle2Icon className="mx-auto h-8 w-8 text-emerald-500" />
                <p className="my-0 mt-3 text-sm font-medium text-gray-900">
                    This page passed every check
                </p>
                <p className="mx-auto my-0 mt-2 max-w-xs text-sm leading-relaxed text-gray-500">
                    The crawl read it and found none of the site audit's findings here. Anything
                    left to improve is on the Proposed changes tab.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <p className="my-0 mb-3 text-sm leading-relaxed text-gray-500">
                Found on this page by the site audit. Each one is fixed here, on the page it
                affects. These are the same fixes the Site Audit tab offers, without leaving
                the page.
            </p>
            {issues.map((issue) => {
                const open = openKey === issue.key;
                return (
                    <div key={issue.key} className="overflow-hidden rounded-lg border border-gray-200">
                        <button
                            type="button"
                            onClick={() => setOpenKey(open ? null : issue.key)}
                            aria-expanded={open}
                            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600"
                        >
                            <AlertTriangleIcon
                                className={cn(
                                    "h-4 w-4 flex-shrink-0",
                                    issue.severity === "error"
                                        ? "text-rose-500"
                                        : issue.severity === "warning"
                                            ? "text-amber-500"
                                            : "text-gray-400",
                                )}
                            />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-gray-900">
                                    {issue.label}
                                </span>
                                <span className="block truncate text-xs text-gray-500">{issue.desc}</span>
                            </span>
                            <ChevronDownIcon
                                className={cn(
                                    "h-4 w-4 flex-shrink-0 text-gray-400 transition-transform",
                                    open && "rotate-180",
                                )}
                            />
                        </button>
                        {open && (
                            <div className="border-t border-gray-100 px-3 py-3">
                                {issue.fix.length > 0 && (
                                    <ul className="mb-4 space-y-1.5">
                                        {issue.fix.map((step, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <CheckIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                <span className="text-xs leading-relaxed text-gray-600">
                                                    {step}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {businessId && pageUrl ? (
                                    <RemediationSection
                                        businessId={businessId}
                                        issueKey={issue.key}
                                        pageUrl={pageUrl}
                                        publishUnsupported={publishUnsupported}
                                    />
                                ) : (
                                    // A fixture page has nothing real to write to.
                                    // The steps above are the whole offer, and the
                                    // button that cannot work is not shown.
                                    <p className="my-0 text-xs leading-relaxed text-gray-500">
                                        Fixes are generated for live businesses only.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function PageWorkshopView({
    url,
    title,
    onBack,
    onOpenPage,
    roleOverride,
    review,
    onReviewChange,
    page: livePage,
    loading = false,
    error = null,
    onGenerate,
    onRedraft,
    onPublish,
    publishBlockedReason,
    publishTargetLabel,
    publishUnsupported,
    pasteOnly,
    onPrecheck,
    businessId,
    pageIssues,
    documentReadAt,
    documentStale,
    documentRefreshing,
    onRefreshDocument,
    flashPlan = false,
    onConfirmChanges,
    onPasteResult,
}: {
    url: string | null;
    title: string;
    /** Back to the crawled-pages list — this IS the page now, not an overlay. */
    onBack: () => void;
    /** Open a different page's workshop, for the far end of a two-way link. */
    onOpenPage?: (url: string, title: string) => void;
    /** The reviewer's correction to the inferred role, if they made one. */
    roleOverride?: PageRole;
    /** This page's review state, owned by the parent so it survives navigation. */
    review: PageReview;
    onReviewChange: (next: PageReview) => void;
    /**
     * The live workshop, when there is one.
     *
     * Omit entirely for a fixture business and the sample page is generated as
     * before. Pass `null` to say "live, but nothing to show" — that renders an
     * empty state rather than falling back to the sample, which on a real
     * client would put invented proposals against real URLs.
     */
    page?: PageWorkshop | null;
    loading?: boolean;
    /** A message the operator can act on, not a stack trace. */
    error?: string | null;
    /**
     * Ask Oura for proposals on this page. Absent on a fixture business.
     *
     * Deliberately an explicit action rather than something that fires on open:
     * generating is a real model call against a real client page, so it costs
     * money and takes time, and the operator should choose to spend it.
     */
    onGenerate?: () => void;
    /**
     * Redraft this page with the reviewer's notes — the whole point of writing
     * them. Same endpoint as onGenerate but steered: each note is resolved
     * against the proposal it names and outranks the model's earlier choices.
     * Absent on a fixture business, where the button stays disabled instead of
     * pretending.
     */
    onRedraft?: (notes: { recommendation_id: string; note: string }[]) => void;
    /**
     * Write the kept proposals to the client's live site (ourteam-backend#4100).
     *
     * Absent hides publishing entirely — a fixture business, or any caller that
     * cannot write. Present means the commit action is offered, behind a
     * confirmation, because it is the one thing on this screen that changes a
     * page the public sees. Resolves with the outcome so the pane can show a
     * drift refusal or a published confirmation in place.
     *
     * `dismissed` is the reviewer's opt-outs; inclusion is the default, so what
     * is sent is what was dropped.
     */
    onPublish?: (args: { draftId: string; dismissed: string[] }) => Promise<AeoPagePublishResult>;
    /**
     * Why publishing is unavailable right now, if it is — no connected website
     * platform, typically. Shown against a disabled Publish rather than hiding
     * it, so the operator learns the step exists and what unlocks it. Absent =
     * publishing is available (subject to there being something to publish).
     */
    publishBlockedReason?: string;
    /** The connected platform a publish writes to, by its own name. Named in
     *  the confirm dialog — hardcoding "WordPress" there was a lie on any
     *  other platform (#3547). */
    publishTargetLabel?: string;
    /** See DraftRow — threaded through unchanged (#3692). */
    publishUnsupported?: (draftType: string) => string | undefined;
    /**
     * A site Oura cannot write to, by its platform's name ("Wix"): Publish
     * becomes "Get the copy to paste", which copies the added changes for a
     * person to paste in. Absent = the site takes a publish.
     */
    pasteOnly?: string;
    /**
     * Checked before the confirm opens, and before the copy is handed over:
     * a refusal (the client's yes is missing) shows at once, in place of a
     * confirm the server would refuse anyway. Null = clear to go on.
     * `ids` are the exact changes being initiated, not the parent's staged
     * state: a stage-and-publish in one event has not re-rendered yet.
     */
    onPrecheck?: (ids: readonly string[]) => Promise<AeoPagePublishResult | null>;
    /** Live business id. Absent (fixture) = the Site audit tab reads but cannot
     *  generate — the same rule the Issues drawer already follows. */
    businessId?: string;
    /**
     * Every Site Audit check THIS page failed.
     *
     * The workshop used to be a two-problem tool: copy, and internal links. Every
     * other finding about the same page — no meta description, no structured
     * data, a duplicate title — lived on a different tab, in a drawer organised
     * by defect rather than by page, so working one page end to end meant
     * leaving the page. The crawl already knows which checks each page failed
     * (`crawledPages[].failed`), and the fix engine is already scoped to exactly
     * (page, issue), so this is a join, not a new capability.
     *
     * `undefined` = this run reported no crawl inventory, so we do not know.
     * `[]` = the crawler read this page and it passed every check. The tab says
     * something different for each, because they are different facts.
     */
    pageIssues?: AuditIssue[];
    /** ISO stamp of when the client's page was last read. Client-side: it is
     *  the age of THIS document, which only the reader can know. */
    documentReadAt?: string;
    /** The last re-read failed, so what is on screen is older than the label
     *  says. Never used to blank the document — only to stop presenting it as
     *  current (#3548). */
    documentStale?: boolean;
    documentRefreshing?: boolean;
    /** Re-read the client's page on demand. Absent on fixtures. */
    onRefreshDocument?: () => void;
    /** Arrived from "Draft them in Page workshop": mark the Answer Plan's proposals for a moment. */
    flashPlan?: boolean;
    /**
     * Ask Oura (PREVIEW): a named person's yes, asked after the precheck and
     * before the copy or the Publish confirm. False = they cancelled, and
     * nothing runs. Absent = no extra step.
     */
    onConfirmChanges?: (ids: readonly string[]) => Promise<boolean>;
    /** Ask Oura (PREVIEW): told whether the copy on a paste-only site worked, after the person's yes. */
    onPasteResult?: (copied: boolean) => void;
}) {
    // Tri-state, and the distinction is load-bearing:
    //   undefined → no live source; generate the sample page (fixture business)
    //   null      → live source, nothing to show (still loading, or failed)
    //   a value   → live data
    // A live business must NEVER fall through to the fixture. Rendering invented
    // proposals against a real client's real URLs is the single worst failure
    // this screen can produce, so "live but unavailable" is an empty state, not
    // a silent sample.
    const page = useMemo(
        () => (livePage !== undefined ? livePage : url ? getPageWorkshop(url, title) : null),
        [livePage, url, title],
    );
    const writesPaused = useWritesPaused();
    const [selectedId, setSelectedId] = useState<string | null>(
        page?.recommendations[0]?.id ?? null,
    );
    const [tab, setTab] = useState<"proposals" | "audit" | "history">("proposals");
    const [flash, setFlash] = useState(ASK_OURA_ON && flashPlan);
    useEffect(() => {
        if (!ASK_OURA_ON || !flashPlan) return;
        setFlash(true);
        const t = window.setTimeout(() => setFlash(false), 1700);
        return () => window.clearTimeout(t);
    }, [flashPlan, url]);
    // The publish flow: the confirm gate, the in-flight flag, and the last
    // outcome kept so its banner stays on screen. All declared up here because
    // hooks cannot live below the early returns further down.
    const [confirmPublish, setConfirmPublish] = useState(false);
    const [publishing, setPublishing] = useState(false);
    /**
     * The last publish result, or null.
     *
     * Cleared whenever the draft changes — a dismiss or a redraft — because the
     * message describes a draft that no longer stands: a drift refusal naming
     * spans the reviewer has since redrafted, or a "published" confirmation for
     * a set they have since edited, would both be lies a moment later.
     */
    const [publishOutcome, setPublishOutcome] = useState<AeoPagePublishResult | null>(null);
    /** What "Get the copy to paste" did, on a site Oura cannot write to. */
    // The outcome travels WITH the sentence (Jack, #3964): a denied clipboard
    // used to read "Your browser did not allow copying" inside a green
    // success box, which says two opposite things at once.
    const [pasteSaid, setPasteSaid] = useState<{ copied: boolean; text: string } | null>(null);

    // Following a link to another page swaps `url` in place rather than
    // remounting (a remount would discard the review), so the selection has
    // to be reset by hand.
    useEffect(() => {
        setSelectedId(page?.recommendations[0]?.id ?? null);
        setTab("proposals");
        // A different page (or a redrafted one) is a different draft; its
        // publish banner and confirm dialog do not belong to it.
        setPublishOutcome(null);
        setConfirmPublish(false);
    }, [url, page]);

    // Nothing is published until the reviewer stages it (#3546). This set holds
    // the opt-INS; it starts empty on every page, which is what stops an unread
    // proposal reaching a client's live site.
    const stagedIds = useMemo(() => new Set(review.staged), [review.staged]);
    const notes = review.notes;

    // THE PAGE ITSELF MUST NOT SCROLL. The breadcrumb, business header and tab
    // bar stay put; only the two panes move, each on its own scrollbar under
    // whichever one the cursor is over. That means this view has to end exactly
    // at the bottom of the viewport, so it measures its own top edge rather
    // than assuming a fixed chrome height — the chrome above it (breadcrumb,
    // header, tabs) is not this component's to know.
    const rootRef = useRef<HTMLDivElement>(null);
    const [viewportHeight, setViewportHeight] = useState<number | null>(null);
    useLayoutEffect(() => {
        const fit = () => {
            const el = rootRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY;
            // The page container's bottom padding, kept as breathing room.
            setViewportHeight(Math.max(520, window.innerHeight - top - 24));
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
        // Depends on `page`, and that is load-bearing rather than tidiness.
        //
        // On a LIVE business the first render has no page yet (the fetch is in
        // flight), so this component returns its loading state and the measured
        // element is not in the tree — `rootRef.current` is null and `fit()`
        // bails. With an empty dependency array the effect never ran again once
        // the page arrived, so the container kept `height: undefined`, the panes
        // had nothing to overflow against, and because the workshop also locks
        // `body.overflow` NOTHING on the screen could scroll. A fixture business
        // never hit it: its page exists on the first render.
    }, [page]);

    // The workshop owns the whole viewport: the two panes scroll, the page
    // behind them does not. Without this the window keeps a scrollbar of its
    // own and the tab bar can drift out of view.
    useLayoutEffect(() => {
        // Only lock the body once the panes actually have a height to scroll
        // within. Locking before that traps the reader: the container has no
        // fixed height, so nothing inside it overflows and nothing scrolls,
        // while the window cannot scroll either. Degrading to "the window
        // scrolls" is worse-looking and completely usable; degrading to
        // "nothing scrolls" is a dead end.
        if (!viewportHeight) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [viewportHeight]);

    // Highlight elements by recommendation id, so the pager can scroll the
    // document to the suggestion it lands on — on a long page the next
    // highlight is often off-screen, and hunting for it defeats the pager.
    const marksRef = useRef(new Map<string, HTMLElement>());
    const registerMark = (id: string, el: HTMLElement | null) => {
        if (el) marksRef.current.set(id, el);
        else marksRef.current.delete(id);
    };
    /**
     * Whether the selected proposal has anywhere to scroll TO (#3780).
     *
     * A proposal only gets a highlight when its text is found in an extracted
     * block. When it is not, `marksRef.get()` returns undefined and the `?.`
     * below makes the scroll a silent no-op — the pager appeared broken because
     * nothing said the target did not exist. This is that missing sentence.
     */
    const [selectedOffPage, setSelectedOffPage] = useState(false);
    useEffect(() => {
        if (!selectedId) { setSelectedOffPage(false); return; }
        const mark = marksRef.current.get(selectedId);
        if (mark) {
            mark.scrollIntoView({ block: "center", behavior: "smooth" });
            setSelectedOffPage(false);
            return;
        }
        setSelectedOffPage(true);
        // `page` is in the deps because the marks re-register when the blocks
        // change; without it a proposal that gained a highlight on reload would
        // keep saying it has none.
    }, [selectedId, page]);

    if (!url) return null;

    // Live business, no page yet. Each of these is a DIFFERENT thing to tell
    // the operator, and collapsing them into one spinner or one blank panel is
    // how "it is loading" and "it failed" become indistinguishable.
    if (!page) {
        return (
            <div className="py-16 text-center">
                {/* A spinner, and copy short enough to sit on one line. The
                    previous sentence wrapped to three with a lone "it." on the
                    last, which reads as a layout fault rather than a wait. */}
                {loading && (
                    <Loader2Icon className="mx-auto mb-3 h-5 w-5 animate-spin text-gray-400" />
                )}
                <p className="my-0 text-sm font-medium text-gray-900">
                    {loading ? "Reading the page" : error ? "Could not read this page" : "Nothing to show yet"}
                </p>
                <p className="mx-auto my-0 mt-1.5 max-w-sm text-sm leading-relaxed text-gray-500">
                    {loading
                        ? "Fetching the live page and any drafts already made for it."
                        : error
                            ? error
                            : "The page could not be loaded, so there is nothing to review."}
                </p>
                <div className="mt-5">
                    <Button variant="secondary" onClick={onBack}>
                        Back to pages
                    </Button>
                </div>
            </div>
        );
    }

    const selected = page.recommendations.find((r) => r.id === selectedId) ?? null;
    const selectedIndex = page.recommendations.findIndex((r) => r.id === selectedId);
    const step = (delta: number) => {
        const n = page.recommendations.length;
        if (n === 0) return;
        const next = ((selectedIndex < 0 ? 0 : selectedIndex) + delta + n) % n;
        setSelectedId(page.recommendations[next].id);
    };
    const href = pageHref(page.url);
    const staged = stagedIds;
    const stagedCount = staged.size;
    const setNotes = (next: Record<string, string>) =>
        onReviewChange({ ...review, notes: next });
    // Budget accounting. The cap is on links this draft would ADD — existing
    // links are left alone (Oura never proposes removing one), so counting
    // them here would penalise a page for links it already earned.
    const role: PageRole = roleOverride ?? page.role;
    const budget = linkBudgetForRole(role);
    const addedLinks = linksAdded(page);
    const toggleStage = (id: string) => {
        // Changing what is staged invalidates any standing publish banner: a
        // "published" confirmation or a drift refusal both describe the set as
        // it was, not as it now is.
        setPublishOutcome(null);
        onReviewChange({
            ...review,
            staged: review.staged.includes(id)
                ? review.staged.filter((x) => x !== id)
                : [...review.staged, id],
        });
    };
    /**
     * Before the confirm: the client's yes first, so a refusal shows before
     * anyone says "Publish". On a site Oura cannot write to, the added
     * changes are copied for a person to paste in instead.
     */
    const startPublish = async (ids: readonly string[]) => {
        setPublishOutcome(null);
        setPasteSaid(null);
        if (onPrecheck) {
            const refused = await onPrecheck(ids);
            if (refused) return setPublishOutcome(refused);
        }
        if (ASK_OURA_ON && onConfirmChanges && !(await onConfirmChanges(ids))) return;
        if (!pasteOnly) return setConfirmPublish(true);
        const words = (page?.recommendations ?? [])
            .filter((r) => ids.includes(r.id))
            .map((r) => (r.kind === "copy" ? r.after : r.kind === "link" && r.requiresNewCopy && r.proposedCopy ? r.proposedCopy : recAnchorText(r)))
            .join("\n\n");
        let copied = false;
        try {
            await navigator.clipboard.writeText(words);
            copied = true;
        } catch {
            copied = false;
        }
        // Ask Oura (PREVIEW): changes from the Answer Plan are never ticked by hand.
        // Oura closes the task when it reads the page and finds the new wording.
        const fromPlan = (page?.recommendations ?? []).filter((r) => ids.includes(r.id) && planOrigin(r)).length;
        const others = ids.length - fromPlan;
        const copiedLine = `Copied ${ids.length} ${ids.length === 1 ? "change" : "changes"}. Paste ${ids.length === 1 ? "it" : "them"} on ${pasteOnly}`;
        const planLine = `Oura then reads the page again and closes the task once the new wording is live (Preview: that re-read is not connected in this build).`;
        setPasteSaid({
            copied,
            text: copied
                ? fromPlan === 0
                    ? `${copiedLine}, then tick each block in Delivery.`
                    : others === 0
                      ? `${copiedLine}. ${planLine}`
                      : `${copiedLine}, then tick the ${others === 1 ? "one" : `${others}`} not from the Answer Plan in Delivery. For the Answer Plan ${fromPlan === 1 ? "change" : "changes"}, ${planLine.charAt(0).toLowerCase()}${planLine.slice(1)}`
                : `Your browser did not allow copying. Select the new wording on the right and copy it, then paste it on ${pasteOnly}.`,
        });
        if (ASK_OURA_ON) onPasteResult?.(copied);
    };
    /** Stage exactly this proposal and open the confirm — the single-fix path. */
    const stageOnlyAndPublish = (id: string) => {
        onReviewChange({ ...review, staged: [id] });
        void startPublish([id]);
    };
    const noteEntries = Object.entries(notes).filter(([, t]) => t.trim().length > 0);
    const canRedraft = noteEntries.length > 0 && !!onRedraft && !loading;

    // Ask Oura (PREVIEW): the Answer Plan's approved answers on this page.
    const planRecs = page.recommendations.filter((r) => planOrigin(r));

    // Publishing is offered only when there is a stored draft to write from,
    // something in it that was not dismissed, and a caller that can write. The
    // reason it is UNAVAILABLE (no connector) is surfaced separately, against a
    // disabled button, rather than by hiding the control.
    const publishedOk = publishOutcome?.ok === true;
    const canPublish =
        !!onPublish
        && !!page.draftId
        && stagedCount > 0
        && !publishBlockedReason
        && !publishing
        && !publishedOk;
    const runPublish = async () => {
        if (!onPublish || !page.draftId) return;
        setPublishing(true);
        // The wire still speaks exclusions, so invert on the way out: everything
        // the reviewer did not stage is dismissed. Publishing 1 of 9 sends 8 ids.
        const dismissed = page.recommendations
            .map((r) => r.id)
            .filter((id) => !staged.has(id));
        const res = await onPublish({ draftId: page.draftId, dismissed });
        setPublishing(false);
        setConfirmPublish(false);
        setPublishOutcome(res);
    };

    return (
        <div
            ref={rootRef}
            style={viewportHeight ? { height: viewportHeight } : undefined}
            className="flex min-h-[520px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
        >
                {/* Body: the whole page on the left, the proposal on the right.
                    There is no header row above these panes: the page identity
                    lives in the right pane, where there was room for it, and a
                    full-width title bar was mostly empty space. */}
                <div className="flex min-h-0 flex-1">
                    {/* Left: the full extracted document */}
                    {/* The document gets the larger share: it carries images,
                        tables and card grids, while the right pane holds one
                        proposal at a time. */}
                    <div className="min-w-0 flex-[3] overflow-y-auto border-r border-gray-200 bg-gray-50/60 px-5 py-5">
                        {/* Centered column: the pane can be very wide on a
                            large screen, but running text stays at a
                            readable measure instead of stretching to it. */}
                        <div className="mx-auto max-w-[900px]">
                            {/* HOW OLD THIS DOCUMENT IS, next to the label that
                                claims it is the page. The reviewer is deciding
                                what to write to a live site from this text; a
                                page edited in WordPress directly changes under
                                us with nothing on screen to notice it (#3548). */}
                            <div className="mb-2.5 flex items-center justify-between gap-3">
                                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                                    <p className="my-0 text-xs font-semibold text-gray-500">
                                        The page as extracted
                                    </p>
                                    {documentReadAt && (
                                        <span
                                            className={cn(
                                                "text-xs",
                                                // A failed re-read is said out
                                                // loud rather than left to look
                                                // like a fresh read: "we tried
                                                // and could not" is the one
                                                // thing a reviewer must not
                                                // have to guess.
                                                documentStale ? "text-amber-700" : "text-gray-400",
                                            )}
                                        >
                                            {documentStale
                                                ? "Could not re-read the page — still showing"
                                                : "Last updated"}{" "}
                                            {versionDate(documentReadAt)}, {timeOfDay(documentReadAt)}
                                        </span>
                                    )}
                                    {onRefreshDocument && (
                                        <button
                                            onClick={onRefreshDocument}
                                            disabled={documentRefreshing}
                                            className="text-xs font-medium text-blue-600 underline-offset-4 transition-colors hover:underline disabled:pointer-events-none disabled:opacity-60"
                                        >
                                            {documentRefreshing ? "Refreshing…" : "Refresh"}
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={onBack}
                                    className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800"
                                >
                                    <ChevronLeftIcon className="h-3.5 w-3.5" />
                                    All crawled pages
                                </button>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white px-4 py-5 sm:px-6">
                                <DocumentPane
                                    page={page}
                                    selectedId={selectedId}
                                    staged={staged}
                                    onSelect={setSelectedId}
                                    registerMark={registerMark}
                                />
                            </div>
                            <p className="mt-3 text-xs leading-relaxed text-gray-400">
                                The whole page, in order, as Oura crawled it. Highlighted text is
                                what Oura proposes to change; the icon in the margin says what
                                kind: a document for copy, a chain for an internal link. Click
                                either to read and review the proposal. A proposal you have added
                                to the changes fills solid; the rest stay a faint wash.
                            </p>
                        </div>
                    </div>

                    {/* Right: which page this is, then the selected proposal */}
                    <div className="flex min-w-0 flex-[2] flex-col">
                        {/* The page identity, fixed above the scrolling
                            proposal — this pane had the room the old header
                            row was wasting. */}
                        <div className="flex-shrink-0 px-4 pb-2 pt-3">
                            <h2 className="truncate text-base font-semibold leading-snug text-gray-900">
                                {page.title}
                            </h2>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                                {/* A text action, not the URL: real paths run
                                    long enough to push everything else off the
                                    line, and the path is already the thing the
                                    reviewer came from. */}
                                {href && (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
                                    >
                                        View page
                                        <ExternalLinkIcon className="h-3 w-3" />
                                    </a>
                                )}
                                <span className="text-gray-300">·</span>
                                {/* The page's ROLE, which is what decides how
                                    many links it may take — a guide links down
                                    to every page in its cluster, an ordinary
                                    page takes two or three. Search metrics used
                                    to sit here too; they belong to choosing a
                                    page, not to working on one, so they stayed
                                    on the crawled-pages table. */}
                                <span className="text-gray-500">{ROLE_LABEL[role]}</span>
                                {/* A NOTICE, not a limit. The two-or-three rule
                                    is an editorial habit from the team's build
                                    sheet, not a health metric — nothing in the
                                    crawl penalises a page for linking well — so
                                    it states the count and the guideline in one
                                    neutral line and restricts nothing. */}
                                <span className="text-gray-300">·</span>
                                <span className="text-gray-500">
                                    {addedLinks} new internal {addedLinks === 1 ? "link" : "links"}{" "}
                                    (Recommended: {budget})
                                </span>
                            </div>
                        </div>

                        {/* Three tabs: what is proposed now, everything else the
                            audit found wrong with this page, and what was
                            proposed before. History was a toggle wedged into
                            the redraft bar, which is neither where you look for
                            it nor what that bar is for.

                            "Site audit" is what makes this panel the page's
                            whole work surface instead of its copy-and-links
                            surface. Its own tab rather than a section under the
                            proposals: a proposal is a sentence Oura wants to
                            change and is reviewed against the document on the
                            left; a check is a property of the page and is not.
                            Reviewing them in one scroll would mix two different
                            kinds of judgement. */}
                        <div className="flex flex-shrink-0 gap-1 border-b border-gray-200 px-4">
                            {([
                                ["proposals", `Proposed changes (${page.recommendations.length})`],
                                // Count only while we HAVE the inventory. On a
                                // run that never reported one, "(0)" would claim
                                // the page is clean, which is a measurement we
                                // did not make.
                                ["audit", pageIssues ? `Site audit (${pageIssues.length})` : "Site audit"],
                                ["history", `History (${page.history.length})`],
                            ] as const).map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setTab(key)}
                                    className={cn(
                                        "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                                        tab === key
                                            ? "border-blue-600 text-blue-700"
                                            : "border-transparent text-gray-500 hover:text-gray-700",
                                    )}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                            {tab === "history" ? (
                                <HistoryTimeline entries={page.history} />
                            ) : tab === "audit" ? (
                                <PageAuditPanel
                                    issues={pageIssues}
                                    pageUrl={url ?? undefined}
                                    businessId={businessId}
                                    publishUnsupported={publishUnsupported}
                                />
                            ) : page.recommendations.length === 0 ? (
                                /* A live page nobody has drafted yet. The document
                                   on the left is real and worth reading on its own,
                                   so this is an invitation rather than an error —
                                   but generating is an explicit click, because it
                                   is a model call against this client's page and it
                                   costs real money each time. */
                                <div className="py-12 text-center">
                                    <p className="my-0 text-sm font-medium text-gray-900">
                                        No proposals for this page yet
                                    </p>
                                    <p className="mx-auto my-0 mt-2 max-w-xs text-sm leading-relaxed text-gray-500">
                                        {onGenerate
                                            ? "Oura has not drafted anything for this page. The page itself is on the left."
                                            : "Nothing has been drafted for this page."}
                                    </p>
                                    {onGenerate && (
                                        <div className="mt-5">
                                            <Button onClick={onGenerate} disabled={loading} writes>
                                                {loading ? "Drafting…" : "Draft proposals"}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                            <>
                            {/* The publish outcome, kept in view until the draft
                                changes. Three different things to say and each
                                gets its own colour: a success, a drift refusal
                                that names the spans and points at Redraft, and
                                anything else the backend said, verbatim. */}
                            {publishOutcome?.ok && (
                                <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                                    <CheckCircle2Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                                    <div className="min-w-0 text-sm text-emerald-800">
                                        <span className="font-medium">
                                            Published to {publishOutcome.via || "the site"}
                                        </span>
                                        {" · "}
                                        {publishOutcome.changes.length}{" "}
                                        {publishOutcome.changes.length === 1 ? "change" : "changes"} live.
                                        {/* No rollback line. `reversible` is true
                                            and the backend means it — POST
                                            /publications/{"{id}"}/revert restores
                                            the post_content it captured. What does
                                            not exist is a route to it from here:
                                            the History tab has no rollback control,
                                            and its entries do not carry the
                                            publication id one would need (#3549,
                                            blocked on ourteam-backend#4621).
                                            Sending the operator to a tab that
                                            cannot do it is the promise this
                                            removes. */}
                                    </div>
                                </div>
                            )}
                            {pasteSaid && (
                                <div role="status" className={cn("mb-4 flex items-start gap-2 rounded-2xl p-4", pasteSaid.copied ? "bg-emerald-50" : "bg-amber-50")}>
                                    <Dot tone={pasteSaid.copied ? "green" : "amber"} className="mt-[7px]" />
                                    <p className={cn("my-0 min-w-0 text-sm leading-relaxed", pasteSaid.copied ? "text-emerald-900" : "text-amber-900")}>{pasteSaid.text}</p>
                                </div>
                            )}
                            {/* H20's gate refused it: name the doc that needs the client's yes (2.23). */}
                            {publishOutcome && !publishOutcome.ok && publishOutcome.refusal && (
                                <PublishRefusalNotice refusal={publishOutcome.refusal} businessId={businessId} onRetry={() => void startPublish(Array.from(staged))} className="mb-4" />
                            )}
                            {/* The client's yes is recorded and nothing went live: the kit's emerald status, not an error. */}
                            {publishOutcome && !publishOutcome.ok && publishOutcome.recorded && (
                                <div role="status" className="mb-4 flex items-start gap-2 rounded-2xl bg-emerald-50 p-4">
                                    <Dot tone="green" className="mt-[7px]" />
                                    <p className="my-0 min-w-0 text-sm leading-relaxed text-emerald-900">{publishOutcome.message}</p>
                                </div>
                            )}
                            {publishOutcome && !publishOutcome.ok && !publishOutcome.refusal && !publishOutcome.recorded && (
                                <div
                                    className={cn(
                                        "mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm",
                                        publishOutcome.kind === "drift"
                                            ? "border-amber-200 bg-amber-50 text-amber-800"
                                            : "border-rose-200 bg-rose-50 text-rose-800",
                                    )}
                                >
                                    <AlertTriangleIcon
                                        className={cn(
                                            "mt-0.5 h-4 w-4 flex-shrink-0",
                                            publishOutcome.kind === "drift" ? "text-amber-600" : "text-rose-600",
                                        )}
                                    />
                                    <div className="min-w-0">
                                        <p className="my-0 leading-relaxed">{publishOutcome.message}</p>
                                        {publishOutcome.kind === "drift" && onRedraft && (
                                            <button
                                                disabled={writesPaused}
                                                onClick={() => onRedraft([])}
                                                className="mt-1 text-xs font-medium text-amber-900 underline underline-offset-4"
                                            >
                                                Redraft against the page as it is now
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {planRecs.length > 0 && (
                                <div
                                    className={cn(
                                        "mb-4 rounded-2xl bg-white p-4 ring-1 ring-black/[0.08]",
                                        flash && "animate-[askPlanWash_1600ms_cubic-bezier(0.2,0.8,0.2,1)_both] motion-reduce:animate-none",
                                    )}
                                    data-plan-proposals
                                >
                                    <style>{PLAN_KEYFRAMES}</style>
                                    <p className="my-0 text-xs text-gray-500">{planOrigin(planRecs[0])}</p>
                                    <p className="my-0 mt-0.5 text-[15px] font-semibold leading-[22px] text-gray-900">
                                        {planRecs.length} approved {planRecs.length === 1 ? "answer" : "answers"} for this page
                                    </p>
                                    <ul className="list-plain my-0 mt-2 space-y-1 p-0">
                                        {planRecs.map((r) => {
                                            const i = page.recommendations.indexOf(r);
                                            const on = r.id === selectedId;
                                            return (
                                                <li key={r.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedId(r.id)}
                                                        className={cn(
                                                            "-mx-2 flex w-[calc(100%+1rem)] items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors",
                                                            on ? "bg-blue-50" : "hover:bg-gray-50",
                                                        )}
                                                    >
                                                        <span className={cn("mt-px text-xs font-medium tabular-nums", on ? "text-blue-700" : "text-gray-400")}>{i + 1}</span>
                                                        {/* Before and after, so both changes read at a glance. */}
                                                        <span className="grid min-w-0 flex-1 grid-cols-[34px_1fr] gap-x-2 gap-y-1 text-[13px] leading-[18px]" data-plan-before-after>
                                                            {r.kind === "copy" && (
                                                                <>
                                                                    <span className="text-xs font-medium text-gray-400">Now</span>
                                                                    <span className="text-gray-500">{r.before}</span>
                                                                </>
                                                            )}
                                                            <span className="text-xs font-medium text-blue-700">New</span>
                                                            <span className="text-gray-900">{r.kind === "copy" ? r.after : recAnchorText(r)}</span>
                                                        </span>
                                                        {staged.has(r.id) && <CheckIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-blue-600" />}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                            {/* One proposal at a time, with a pager — stepping
                                also scrolls the document to the highlight, so
                                the pager doubles as navigation on a long page. */}
                            <div className={ASK_OURA_ON ? "mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-gray-100 pb-2.5" : "mb-4 flex items-center justify-between gap-3 border-b border-gray-100 pb-2.5"}>
                                <p className={ASK_OURA_ON ? "my-0 min-w-0 whitespace-nowrap text-sm font-semibold text-gray-900" : "my-0 min-w-0 truncate text-sm font-semibold text-gray-900"}>
                                    Proposal {(selectedIndex < 0 ? 0 : selectedIndex) + 1}
                                    <span className="font-normal text-gray-400">
                                        {" "}of {page.recommendations.length} ·{" "}
                                        {stagedCount} added
                                    </span>
                                </p>
                                <div className="flex flex-shrink-0 items-center gap-2">
                                    {/* The staging decision lives on the title row:
                                        it is a decision about THIS proposal, so it
                                        belongs with its name. It carries primary
                                        weight now (#3546) — under opt-out this was a
                                        de-emphasised "Dismiss", which put the low-cost
                                        action in the loud position and the costly one
                                        in the default. */}
                                    {selected && (
                                        <Button
                                            size="sm"
                                            variant={staged.has(selected.id) ? "outline" : undefined}
                                            onClick={() => toggleStage(selected.id)}
                                        >
                                            {staged.has(selected.id) ? (
                                                <>
                                                    <CheckIcon className="h-3.5 w-3.5" />
                                                    Added
                                                </>
                                            ) : (
                                                "Add to changes"
                                            )}
                                        </Button>
                                    )}
                                    {/* The single-fix path: most reviews are one
                                        proposal, and making that case walk through
                                        staging first would be ceremony. Still goes
                                        through the confirm dialog. */}
                                    {selected && onPublish && !publishedOk && (
                                        <button
                                            disabled={writesPaused}
                                            onClick={() => stageOnlyAndPublish(selected.id)}
                                            className="text-xs font-medium text-gray-500 underline-offset-4 transition-colors hover:text-gray-800 hover:underline"
                                        >
                                            {pasteOnly ? "Get the copy to paste" : "Publish this change"}
                                        </button>
                                    )}
                                    {page.recommendations.length > 1 && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => step(-1)}
                                                aria-label="Previous suggestion"
                                                className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                            >
                                                <ChevronLeftIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => step(1)}
                                                aria-label="Next suggestion"
                                                className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                            >
                                                <ChevronRightIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* THE PAGER SAYS WHEN IT CANNOT JUMP (#3780). Before
                                this, a proposal whose text is not in the extracted
                                page scrolled nowhere and highlighted nothing, and
                                the pager read as broken. It is not broken — there
                                is genuinely nothing on the left to point at, and
                                the reason is worth knowing: it usually means the
                                live page changed after the scan that proposed
                                this. */}
                            {selected && selectedOffPage && (
                                <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                    <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                    <span>
                                        This text is not on the page as extracted, so there is nothing to
                                        jump to on the left. The page has probably changed since the scan
                                        that proposed it. Re-run the scan before publishing this one.
                                    </span>
                                </p>
                            )}
                            {selected ? (
                                <RecommendationCard
                                    key={selected.id}
                                    rec={selected}
                                    washed={flash && !!planOrigin(selected)}
                                    ouraSuggests={planRecs.length > 0}
                                    blockText={
                                        page.blocks.find((b) => b.id === selected.blockId)?.text ?? ""
                                    }
                                    staged={staged.has(selected.id)}
                                    note={notes[selected.id] ?? ""}
                                    onNote={(text) => setNotes({ ...notes, [selected.id]: text })}
                                    onOpenPage={onOpenPage}
                                />
                            ) : (
                                <p className="text-sm text-gray-500">
                                    Click a highlight on the left to see what Oura proposes for it.
                                </p>
                            )}
                            </>
                            )}
                        </div>

                        {/* THE REDRAFT BAR. One queue, not two boxes: the notes
                            the reviewer wrote per proposal collect here and go
                            up together in ONE redraft. Batching is deliberate —
                            a redraft supersedes the whole draft on the backend
                            (ourteam-backend#4003), so firing one per note would
                            churn versions and make her wait between edits. A
                            note that applies to the whole page is written on
                            any proposal and marked as such. */}
                        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3">
                            {/* THE STAGED SET. What Publish is about to write,
                                spelled out next to the button that writes it.
                                Each chip is removable, so the last chance to
                                take something back does not require scrolling
                                the proposals list to find it again. */}
                            {onPublish && stagedCount > 0 && (
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                    {page.recommendations.map((r, i) => {
                                        if (!staged.has(r.id)) return null;
                                        const anchor = recAnchorText(r);
                                        return (
                                            <span
                                                key={r.id}
                                                className="inline-flex max-w-full items-center gap-1 rounded-full border border-blue-200 bg-blue-50 py-0.5 pl-2 pr-1 text-xs text-blue-700"
                                            >
                                                <button
                                                    onClick={() => setSelectedId(r.id)}
                                                    className="max-w-[16rem] truncate"
                                                    title={`${KIND_LABEL[r.kind]} — ${anchor}`}
                                                >
                                                    <span className="text-blue-400">{i + 1}</span>{" "}
                                                    {anchor}
                                                </button>
                                                <button
                                                    onClick={() => toggleStage(r.id)}
                                                    aria-label={`Remove change ${i + 1} from the set`}
                                                    className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-blue-100"
                                                >
                                                    <XIcon className="h-3 w-3" />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {noteEntries.length > 0 && (
                                <div className="mb-2 space-y-1">
                                    {noteEntries.map(([id, text]) => {
                                        const idx =
                                            page.recommendations.findIndex((r) => r.id === id) + 1;
                                        return (
                                            <button
                                                key={id}
                                                onClick={() => setSelectedId(id)}
                                                className="block w-full truncate text-left text-xs text-gray-600 transition-colors hover:text-gray-900"
                                                title={text}
                                            >
                                                <span className="text-gray-400">Proposal {idx}</span> ·{" "}
                                                {text}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-2 text-xs text-gray-400">
                                    {/* Ask Oura's longer copy result wraps to two lines. */}
                                    <span className={ASK_OURA_ON ? "line-clamp-2" : "truncate"}>
                                        {/* When publishing is blocked, that is the
                                            single most useful thing to say here —
                                            it names what the reviewer would reach
                                            for and cannot. Otherwise the ordinary
                                            state note. */}
                                        {onPublish && publishBlockedReason
                                            ? publishBlockedReason
                                            : noteEntries.length > 0
                                                ? `${noteEntries.length} ${noteEntries.length === 1 ? "note" : "notes"} to apply`
                                                : onPublish
                                                    ? stagedCount > 0
                                                        ? `${stagedCount} ${stagedCount === 1 ? "change" : "changes"} added, ready to publish`
                                                        : "Add a change to publish"
                                                    : "Nothing publishes until you add it."}
                                    </span>
                                </div>
                                <div className="flex flex-shrink-0 items-center gap-2">
                                    <Button
                                        variant={onPublish ? "secondary" : "default"}
                                        disabled={!canRedraft}
                                        writes
                                        onClick={() =>
                                            onRedraft?.(
                                                noteEntries.map(([id, text]) => ({
                                                    recommendation_id: id,
                                                    note: text.trim(),
                                                })),
                                            )
                                        }
                                    >
                                        <SparklesIcon className="h-4 w-4" />
                                        {loading && noteEntries.length > 0
                                            ? "Redrafting…"
                                            : `Redraft${noteEntries.length > 0 ? ` (${noteEntries.length})` : ""}`}
                                    </Button>
                                    {/* The commit. Primary, and to the RIGHT of
                                        Redraft, because the reviewer revises
                                        first and publishes last. Behind a
                                        confirmation — this is the one control
                                        here that changes a page the public sees. */}
                                    {onPublish && pasteOnly && (
                                        <Button
                                            disabled={stagedCount === 0 || publishing}
                                            writes
                                            onClick={() => void startPublish(Array.from(staged))}
                                        >
                                            <ClipboardCopyIcon className="h-4 w-4" />
                                            Get the copy to paste
                                        </Button>
                                    )}
                                    {onPublish && !pasteOnly && (
                                        <Button
                                            disabled={!canPublish}
                                            writes
                                            onClick={() => void startPublish(Array.from(staged))}
                                        >
                                            {publishing ? (
                                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <UploadCloudIcon className="h-4 w-4" />
                                            )}
                                            {/* The button states its own scope. A
                                                bare "Publish" made a nine-proposal
                                                write look identical to a one-proposal
                                                write. */}
                                            {publishedOk
                                                ? "Published"
                                                : stagedCount > 0
                                                    ? `Publish ${stagedCount} ${stagedCount === 1 ? "change" : "changes"}`
                                                    : "Publish"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            {confirmPublish && (
                <PublishConfirm
                    title={page.title}
                    url={page.url}
                    count={stagedCount}
                    via={publishBlockedReason ? "" : publishTargetLabel ?? ""}
                    busy={publishing}
                    onConfirm={() => void runPublish()}
                    onClose={() => setConfirmPublish(false)}
                />
            )}
        </div>
    );
}

/**
 * The gate in front of a live write.
 *
 * Publishing is the one action in the workshop that changes a page the public
 * sees, so it does not happen on a single click. The dialog states the two
 * things that decide whether it is the right button: which page, and how many
 * changes.
 *
 * It used to state a third — that the write could be rolled back from the
 * History tab. There is no rollback control in the History tab, and no code
 * path that could reach one (#3549). Promising an undo the product cannot
 * perform is worse than offering none: it is precisely the sentence that turns
 * a nervous yes into a confident one, on a write to a client's production
 * site. The line comes back when the control does.
 *
 * The count is the STAGED proposals, not the total: nothing else is being
 * published, and saying "12 changes" over a draft of which the reviewer added
 * four would overstate what is about to happen.
 */
function PublishConfirm({
    title,
    url,
    count,
    via,
    busy,
    onConfirm,
    onClose,
}: {
    title: string;
    url: string;
    count: number;
    via: string;
    busy: boolean;
    onConfirm: () => void;
    onClose: () => void;
}) {
    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold leading-snug text-gray-900">
                            Publish to {via || "the live site"}
                        </h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            This writes the changes you added to the page your visitors see.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <div className="truncate text-sm font-medium text-gray-900">{title}</div>
                    <div className="mt-0.5 break-all text-xs text-gray-500">{url}</div>
                </div>

                <p className="mt-3 text-sm text-gray-600">
                    {count} {count === 1 ? "change" : "changes"} will go live. Anything you dismissed
                    stays out.
                </p>

                <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={onConfirm} disabled={busy} writes>
                        {busy && <Loader2Icon className="h-4 w-4 animate-spin" />}
                        Publish {count} {count === 1 ? "change" : "changes"}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
